import axios from 'axios';
import axiosWrapper from './axiosWrapper';
import cacheSync from '../cache/cacheSync';
import getApiBaseUrl from '../getApiBaseUrl';

/**
 * Import service for document import operations
 */
const importService = {
  /**
   * Preview a document to detect chapters and get word counts
   * @param {File} file - The document file to preview
   * @param {Object} options - Preview options
   * @returns {Promise} - Response with preview data
   */
  async previewDocument(file, options = {}) {
    const formData = new FormData();
    formData.append('document', file);

    if (options.chapterPattern) {
      formData.append('chapterPattern', options.chapterPattern);
    }
    if (options.usePageBreaks !== undefined) {
      formData.append('usePageBreaks', options.usePageBreaks.toString());
    }
    if (options.skipTitlePage !== undefined) {
      formData.append('skipTitlePage', options.skipTitlePage.toString());
    }

    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'multipart/form-data',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await axios({
        method: 'post',
        url: `${getApiBaseUrl()}import/preview`,
        data: formData,
        headers,
        withCredentials: true,
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Import a document into a project
   * @param {string} projectId - The project ID to import into
   * @param {File} file - The document file to import
   * @param {Object} options - Import options
   * @returns {Promise} - Response with import results
   */
  async importDocument(projectId, file, options = {}) {
    const formData = new FormData();
    formData.append('document', file);

    // Formatting options
    if (options.bold !== undefined) {
      formData.append('bold', options.bold.toString());
    }
    if (options.italic !== undefined) {
      formData.append('italic', options.italic.toString());
    }
    if (options.underline !== undefined) {
      formData.append('underline', options.underline.toString());
    }
    if (options.strikethrough !== undefined) {
      formData.append('strikethrough', options.strikethrough.toString());
    }
    if (options.highlight !== undefined) {
      formData.append('highlight', options.highlight.toString());
    }
    if (options.color !== undefined) {
      formData.append('color', options.color.toString());
    }
    if (options.tab !== undefined) {
      formData.append('tab', options.tab.toString());
    }

    // Chapter detection options
    if (options.chapterPattern) {
      formData.append('chapterPattern', options.chapterPattern);
    }
    if (options.usePageBreaks !== undefined) {
      formData.append('usePageBreaks', options.usePageBreaks.toString());
    }
    if (options.skipTitlePage !== undefined) {
      formData.append('skipTitlePage', options.skipTitlePage.toString());
    }

    // Extraction options (characters / timeline from manuscript analysis)
    if (options.importCharacters !== undefined) {
      formData.append('importCharacters', options.importCharacters.toString());
    }
    if (options.importTimeline !== undefined) {
      formData.append('importTimeline', options.importTimeline.toString());
    }

    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'multipart/form-data',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await axios({
        method: 'post',
        url: `${getApiBaseUrl()}projects/${projectId}/import`,
        data: formData,
        headers,
        withCredentials: true,
      });

      // Update project cache after successful import
      // The import response includes the updated project with new chapters
      if (response.data && response.data.project) {
        const projectResponse = {
          data: {
            data: {
              project: response.data.project,
            },
          },
        };
        cacheSync.syncAfterMutation('projects', projectResponse, 'update').catch((err) => {
          console.warn('[importService] Error updating cache after import:', err);
        });
      }

      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Analyze a single text chunk with the AI service.
   * @param {string} projectId - Project the import belongs to
   * @param {Object} payload - { text, extract: {characters, timeline, relationships}, knownCharacters }
   */
  async analyzeChunk(projectId, payload) {
    return axiosWrapper({
      method: 'post',
      path: `projects/${projectId}/import/analyze-chunk`,
      data: payload,
      optimistic: false,
    });
  },

  /**
   * Persist merged extraction results (characters, families, timeline events).
   * @param {string} projectId - Project the import belongs to
   * @param {Object} payload - { characters, timelineEvents, relationships }
   */
  async applyExtractions(projectId, payload) {
    return axiosWrapper({
      method: 'post',
      path: `projects/${projectId}/import/apply-extractions`,
      data: payload,
      optimistic: false,
    });
  },
};

export default importService;
